import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

type NotificationPreferenceMap = Record<string, Record<string, boolean>>;

export interface UseNotificationPreferencesResult {
  preferences: NotificationPreferenceMap;
  isEnabled: (event: string, channel: string) => boolean;
  updatePreference: (event: string, channel: string, enabled: boolean) => void;
  loading: boolean;
}

interface NotificationPreferenceRow {
  event: string;
  channel: string;
  enabled: boolean;
}

/**
 * useNotificationPreferences — manages per-event per-channel preferences.
 * Default for any event/channel not in DB: enabled = true (opt-out model).
 */
export function useNotificationPreferences(
  userId: string | null | undefined
): UseNotificationPreferencesResult {
  const [preferences, setPreferences] = useState<NotificationPreferenceMap>({});
  const [loading, setLoading] = useState<boolean>(true);
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // ── Load preferences ────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    setLoading(true);

    supabase
      .from('notification_preferences')
      .select('event, channel, enabled')
      .eq('user_id', userId)
      .then(({ data, error }) => {
        if (error) {
          console.error('[Arbi] notification prefs fetch error:', error.message);
          setLoading(false);
          return;
        }
        // Build lookup: { [event]: { in_app: bool, email: bool, ... } }
        const map: NotificationPreferenceMap = {};
        const rows = (data ?? []) as unknown as NotificationPreferenceRow[];
        for (const row of rows) {
          if (!map[row.event]) {
            map[row.event] = {};
          }
          map[row.event][row.channel] = row.enabled;
        }
        setPreferences(map);
        setLoading(false);
      });
  }, [userId]);

  // ── Get preference value (defaults to true if not set) ──────
  const isEnabled = useCallback(
    (event: string, channel: string): boolean => {
      return preferences[event]?.[channel] ?? true;
    },
    [preferences]
  );

  // ── Update preference (debounced upsert, optimistic UI) ─────
  const updatePreference = useCallback(
    (event: string, channel: string, enabled: boolean): void => {
      // Optimistic update
      setPreferences((prev) => ({
        ...prev,
        [event]: { ...(prev[event] || {}), [channel]: enabled },
      }));

      // Debounce the DB upsert
      const key = `${event}:${channel}`;
      if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);

      debounceTimers.current[key] = setTimeout(async () => {
        const { error } = await supabase.from('notification_preferences').upsert(
          {
            user_id: userId,
            event,
            channel,
            enabled,
            updated_at: new Date().toISOString(),
          } as never,
          { onConflict: 'user_id,event,channel' }
        );
        if (error) {
          console.error('[Arbi] notification pref upsert error:', error.message);
          // Roll back on error
          setPreferences((prev) => ({
            ...prev,
            [event]: { ...(prev[event] || {}), [channel]: !enabled },
          }));
        }
      }, 300);
    },
    [userId]
  );

  return { preferences, isEnabled, updatePreference, loading };
}
